import React from 'react';
import { Helmet } from "react-helmet";

const RefundPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Tanarela - Premium Landscape Design Materials Australia</title>
        <meta name="description" content="Tanarela - Premium landscape design materials and natural stone products for creating stunning outdoor spaces in Australia" />
      </Helmet>

      <div className="policy-container">
      <div className="policy-content">
        <h1>Refund Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            At Tanarela, we are committed to providing premium quality landscape design materials and natural stone products 
            that meet your expectations. We understand that sometimes products may not be suitable for your needs, and we want 
            to ensure you have a positive shopping experience with us.
          </p>
          <p>
            This Refund Policy outlines the terms and conditions for returns, refunds, exchanges, and cancellations. Please read 
            this policy carefully before making a purchase. By purchasing from Tanarela, you acknowledge and agree to the terms 
            set forth in this policy.
          </p>
        </section>

        <section>
          <h2>2. Your Satisfaction is Our Priority</h2>
          <p>
            We take great care in sourcing and delivering high-quality landscape materials and natural stone products. However, 
            we recognize that natural stone products can vary in appearance, color, texture, and pattern due to their organic nature. 
            We encourage you to carefully review product descriptions, specifications, and images before placing an order.
          </p>
          <p>
            If you are unsure about a product's suitability for your project, please contact our customer support team before 
            purchasing. We are happy to provide additional information, samples (where available), or recommendations.
          </p>
        </section>

        <section>
          <h2>3. Return Eligibility</h2>
          
          <h3>3.1 General Return Conditions</h3>
          <p>To be eligible for a return, the following conditions must be met:</p>
          <ul>
            <li>The return request must be initiated within 30 days of the delivery date</li>
            <li>Products must be unused, undamaged, and in their original condition</li>
            <li>Products must be in their original packaging with all labels, tags, and documentation intact</li>
            <li>You must provide proof of purchase (order number, receipt, or confirmation email)</li>
            <li>Custom-made, personalized, or specially ordered products are not eligible for return unless defective</li>
          </ul>

          <h3>3.2 Non-Returnable Items</h3>
          <p>The following items are not eligible for return or refund:</p>
          <ul>
            <li>Custom-cut or specially ordered natural stone products</li>
            <li>Products that have been installed, used, or altered in any way</li>
            <li>Products with signs of wear, damage, or improper storage</li>
            <li>Products purchased during clearance, final sale, or promotional events (unless otherwise stated)</li>
            <li>Products without original packaging or missing components</li>
            <li>Products that have been exposed to environmental conditions (weather, moisture, chemicals)</li>
            <li>Digital products, gift cards, or services</li>
          </ul>

          <h3>3.3 Natural Variations</h3>
          <p>
            Due to the natural characteristics of stone products, variations in color, veining, texture, and pattern are expected 
            and are not considered defects. Returns based solely on natural variations will be evaluated on a case-by-case basis.
          </p>
        </section>

        <section>
          <h2>4. Damaged or Defective Products</h2>
          
          <h3>4.1 Inspection Upon Delivery</h3>
          <p>
            We carefully inspect and package all products before shipment. However, damage may occur during transit. 
            Please inspect your order immediately upon delivery and note any visible damage on the delivery receipt.
          </p>

          <h3>4.2 Reporting Damage</h3>
          <p>
            If you receive damaged or defective products, you must notify us within 48 hours of delivery. Please contact our 
            customer support team with the following information:
          </p>
          <ul>
            <li>Order number and delivery date</li>
            <li>Detailed description of the damage or defect</li>
            <li>Clear photographs showing the damaged/defective product and packaging</li>
            <li>Copy of the delivery receipt (if damage was noted)</li>
          </ul>

          <h3>4.3 Resolution for Damaged Products</h3>
          <p>
            Once we receive your damage claim and verify the information, we will offer one of the following solutions:
          </p>
          <ul>
            <li>Full replacement of the damaged product at no additional cost</li>
            <li>Partial refund if replacement is not desired</li>
            <li>Full refund if replacement is unavailable</li>
          </ul>
          <p>
            We will arrange for the damaged product to be picked up or provide a prepaid return shipping label. 
            You will not be responsible for return shipping costs for damaged or defective products.
          </p>
        </section>

        <section>
          <h2>5. Return Process</h2>
          
          <h3>5.1 Initiating a Return</h3>
          <p>To initiate a return, please follow these steps:</p>
          <ol>
            <li>Contact our customer support team via email at <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a> or through our website contact form</li>
            <li>Provide your order number, reason for return, and any relevant photos</li>
            <li>Wait for return authorization (RMA) approval from our team</li>
            <li>Once approved, you will receive return instructions and a Return Merchandise Authorization (RMA) number</li>
            <li>Package the product securely in its original packaging</li>
            <li>Include the RMA number on the outside of the package</li>
            <li>Ship the product to the address provided using a trackable shipping method</li>
          </ol>

          <h3>5.2 Return Authorization Required</h3>
          <p>
            <strong>Important:</strong> Do not return products without first obtaining a Return Merchandise Authorization (RMA) number. 
            Unauthorized returns may be refused or result in delays in processing your refund.
          </p>

          <h3>5.3 Return Shipping Costs</h3>
          <p>Return shipping costs depend on the reason for the return:</p>
          <ul>
            <li><strong>Damaged or Defective Products:</strong> We will provide a prepaid return shipping label or arrange pickup at no cost to you</li>
            <li><strong>Change of Mind / Wrong Order:</strong> You are responsible for return shipping costs unless otherwise agreed</li>
            <li><strong>Wrong Item Sent by Us:</strong> We will cover all return shipping costs and expedite a replacement</li>
          </ul>

          <h3>5.4 Packaging Requirements</h3>
          <p>
            Products must be securely packaged to prevent damage during return shipment. We recommend using the original 
            packaging materials. If the product is damaged during return shipment due to inadequate packaging, you may be held 
            responsible for the loss in value.
          </p>
        </section>

        <section>
          <h2>6. Refund Processing</h2>
          
          <h3>6.1 Inspection and Approval</h3>
          <p>
            Once we receive your returned product, our team will inspect it to ensure it meets the return eligibility criteria. 
            This process typically takes 3-5 business days. We will notify you via email once the inspection is complete.
          </p>

          <h3>6.2 Refund Issuance</h3>
          <p>
            If your return is approved, we will process your refund using the original payment method. Refund processing times are:
          </p>
          <ul>
            <li>Refund initiated: Within 3-5 business days after approval</li>
            <li>Credit card refunds: 5-10 business days (depending on your bank)</li>
            <li>PayPal refunds: 3-5 business days</li>
            <li>Other payment methods: As specified by the payment provider</li>
          </ul>

          <h3>6.3 Refund Amount</h3>
          <p>
            Your refund will include the purchase price of the returned product(s). Shipping costs are generally non-refundable 
            unless the return is due to our error or a defective product. If you received free shipping on your original order, 
            the actual shipping cost may be deducted from your refund.
          </p>

          <h3>6.4 Partial Refunds</h3>
          <p>In certain situations, only partial refunds may be granted:</p>
          <ul>
            <li>Products showing signs of use or installation</li>
            <li>Products damaged due to improper return packaging</li>
            <li>Products returned without original packaging or components</li>
            <li>Products returned more than 30 days after delivery (at our discretion)</li>
          </ul>

          <h3>6.5 Non-Receipt of Refund</h3>
          <p>
            If you have not received your refund within the expected timeframe, please:
          </p>
          <ul>
            <li>Check your bank account or credit card statement again</li>
            <li>Contact your bank or credit card company (processing may take time)</li>
            <li>Contact your payment provider</li>
            <li>If you've done all of this and still have not received your refund, please contact us at <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a></li>
          </ul>
        </section>

        <section>
          <h2>7. Exchanges</h2>
          
          <h3>7.1 Exchange Policy</h3>
          <p>
            We offer exchanges for products of equal or greater value. If you wish to exchange a product for a different size, 
            color, or type, please contact our customer support team to arrange the exchange.
          </p>

          <h3>7.2 Exchange Process</h3>
          <p>To exchange a product:</p>
          <ol>
            <li>Contact us within 30 days of delivery to request an exchange</li>
            <li>Specify the product you wish to receive in exchange</li>
            <li>Follow the return process outlined in Section 5</li>
            <li>Once we receive and inspect the returned product, we will ship the replacement item</li>
            <li>If the replacement product is of higher value, you will be charged the difference</li>
            <li>If the replacement product is of lower value, we will refund the difference</li>
          </ol>

          <h3>7.3 Exchange Shipping</h3>
          <p>
            Original shipping costs for exchanges are generally non-refundable. You may be responsible for shipping costs to 
            return the original product, and we will cover shipping costs for the replacement item (unless otherwise stated).
          </p>
        </section>

        <section>
          <h2>8. Order Cancellations</h2>
          
          <h3>8.1 Cancellation Before Shipment</h3>
          <p>
            You may cancel your order before it has been shipped for a full refund. To cancel an order, please contact us 
            immediately at <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a> with your order number. If your order has already been processed or shipped, 
            cancellation may not be possible, and you will need to follow the return process.
          </p>

          <h3>8.2 Cancellation After Shipment</h3>
          <p>
            Once an order has been shipped, it cannot be cancelled. You will need to wait to receive the product and then 
            initiate a return following our return process.
          </p>

          <h3>8.3 Cancellation by Tanarela</h3>
          <p>
            We reserve the right to cancel orders in certain circumstances, including:
          </p>
          <ul>
            <li>Product unavailability or stock issues</li>
            <li>Pricing errors or system malfunctions</li>
            <li>Suspected fraudulent or unauthorized transactions</li>
            <li>Inability to verify customer information</li>
            <li>Force majeure events</li>
          </ul>
          <p>
            If we cancel your order, we will notify you promptly and issue a full refund of any payment received.
          </p>
        </section>

        <section>
          <h2>9. Wrong Item Shipped</h2>
          <p>
            If we shipped the wrong product to you, please contact us immediately. We will:
          </p>
          <ul>
            <li>Arrange for the incorrect item to be returned at our expense</li>
            <li>Ship the correct product to you at no additional cost</li>
            <li>Expedite the replacement shipment</li>
            <li>Provide a refund if the correct item is unavailable</li>
          </ul>
          <p>
            Please provide photographic evidence of the wrong item received to help us resolve the issue quickly.
          </p>
        </section>

        <section>
          <h2>10. Bulk and Commercial Orders</h2>
          <p>
            Returns and refunds for bulk orders or commercial purchases may be subject to different terms. Please contact our 
            commercial sales team for specific return policies applicable to large-volume orders.
          </p>
          <p>
            Custom orders, project-specific materials, and specially sourced products may have limited or no return eligibility. 
            We recommend discussing your project requirements with our team before placing large orders.
          </p>
        </section>

        <section>
          <h2>11. Australian Consumer Law</h2>
          <p>
            Nothing in this Refund Policy excludes, restricts, or modifies your rights under the Australian Consumer Law or 
            other applicable consumer protection laws. You are entitled to remedies for products that:
          </p>
          <ul>
            <li>Are not of acceptable quality</li>
            <li>Do not match the description provided</li>
            <li>Are not fit for the purpose for which they are commonly used</li>
            <li>Are not fit for a specific purpose you made known to us</li>
          </ul>
          <p>
            Under Australian Consumer Law, consumers have the right to a repair, replacement, or refund for major failures, 
            and compensation for reasonably foreseeable loss or damage. These guarantees cannot be excluded.
          </p>
        </section>

        <section>
          <h2>12. Store Credit</h2>
          <p>
            In some cases, we may offer store credit as an alternative to a refund. Store credit:
          </p>
          <ul>
            <li>Can be used for future purchases on our website</li>
            <li>Does not expire unless otherwise stated</li>
            <li>Is non-transferable and cannot be exchanged for cash</li>
            <li>Will be issued in the original purchase amount</li>
          </ul>
        </section>

        <section>
          <h2>13. Restocking Fees</h2>
          <p>
            For certain product categories or special orders, a restocking fee of up to 20% may apply for returns that are not 
            due to defects or errors on our part. Any applicable restocking fees will be communicated to you when you request 
            a return authorization.
          </p>
        </section>

        <section>
          <h2>14. Seasonal and Clearance Items</h2>
          <p>
            Products marked as "Final Sale," "Clearance," or "As-Is" are generally not eligible for return or refund unless 
            they are defective or we made an error. These restrictions will be clearly indicated on the product page at the time of purchase.
          </p>
        </section>

        <section>
          <h2>15. Installation and Professional Services</h2>
          <p>
            If you have hired a professional to install our products, we recommend waiting until your installer has inspected 
            and approved the materials before installation. We cannot accept returns for products that have been installed.
          </p>
          <p>
            We are not responsible for installation errors, labor costs, or consequential damages arising from the use of our products.
          </p>
        </section>

        <section>
          <h2>16. Gift Purchases</h2>
          <p>
            Products purchased as gifts can be returned by the recipient following our standard return process. Refunds for 
            gift purchases will be issued as store credit unless proof of purchase is provided by the original purchaser.
          </p>
        </section>

        <section>
          <h2>17. International Returns</h2>
          <p>
            Currently, we primarily serve customers within Australia. International returns may be subject to additional terms 
            and conditions, including customs duties, import taxes, and extended processing times. Please contact us for assistance 
            with international returns.
          </p>
        </section>

        <section>
          <h2>18. Contact Us for Returns and Refunds</h2>
          <p>
            If you have any questions about our Refund Policy or need assistance with a return, please contact our customer 
            support team:
          </p>
          <div className="contact-info">
            <p><strong>Tanarela Returns Department</strong></p>
            <p>Email: <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a></p>
            <p>Customer Support: <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a></p>
            <p>Website: https://tanarela.com</p>
            <p>Address: Australia</p>
          </div>
          <p>
            Our customer support team is available to assist you and will work to resolve your concerns promptly and fairly. 
            We typically respond to return requests within 1-2 business days.
          </p>
        </section>

        <section>
          <h2>19. Updates to This Policy</h2>
          <p>
            We reserve the right to update or modify this Refund Policy at any time. Changes will be effective immediately upon 
            posting on our website with an updated "Last Updated" date. We encourage you to review this policy periodically.
          </p>
          <p>
            Your continued use of our website and services after changes are posted constitutes your acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>20. Fair Trading Commitment</h2>
          <p>
            At Tanarela, we are committed to fair and ethical business practices. We value your trust and strive to provide 
            excellent customer service at every stage of your shopping experience. If you are dissatisfied with any aspect of 
            your purchase, please give us the opportunity to make it right.
          </p>
          <p>
            Thank you for choosing Tanarela for your landscape design and natural stone needs. We appreciate your business and 
            look forward to serving you.
          </p>
        </section>
      </div>
    </div>
  
    </>);

};

export default RefundPolicy;